import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  ErrorMessage,
  Field,
  FieldGroup,
  Label,
} from "../../components/catalyst/fieldset";
import { Input } from "../../components/catalyst/input";
import {
  PencilSquareIcon,
  ArrowDownOnSquareIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { Heading } from "../../components/Heading";
import { IconButton } from "../../components/IconButton";
import { FormSection } from "../../components/FormSection";
import useAuthStore from "../../store/authStore";
import {
  Listbox,
  ListboxLabel,
  ListboxOption,
} from "../../components/catalyst/listbox";
import { ReadOnlyFormElement } from "../../components/ReadOnlyFormElement";
import { Text } from "../../components/catalyst/text";
import VerifyEmail from "../../components/verifyEmail/VerifyEmail";
import Avatar from "../../components/Avatar";
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const CurrentUser: React.FC = () => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    reset,
    setError,
    clearErrors,
  } = useForm();

  const { currentUser, updateUser } = useAuthStore();

  const [isReadOnly, setIsReadOnly] = useState(true);
  const [filePreview, setFilePreview] = useState<any>(null);

  const setEditMode = () => {
    reset({
      title: currentUser?.title,
      firstName: currentUser?.firstName,
      lastName: currentUser?.lastName,
      phone: currentUser?.phone,
      profilePicture: currentUser?.profilePicture,
    });
    setIsReadOnly(false);
  };

  const cancelEdit = () => {
    reset();
    setIsReadOnly(true);
    setFilePreview(null); // Reset preview
  };

  const onSubmit = (data: any) => {
    const ppData =
      typeof data.profilePicture === "string"
        ? undefined
        : data.profilePicture[0];

    const registerVals = {
      ...data,
      profilePicture: currentUser?.profilePicture,
      updatedProfilePicture: ppData,
    };

    updateUser(registerVals);
    setIsReadOnly(true);
    setFilePreview(null); // Reset preview
  };

  const handleFileChange = (event: any) => {
    const selectedFile = event.target.files[0];

    if (selectedFile) {
      // Validate file size
      if (selectedFile.size > MAX_FILE_SIZE) {
        setError("profilePicture", {
          type: "manual",
          message: "File size exceeds 10MB. Please choose a smaller file.",
        });
        setFilePreview(null); // Reset preview
      } else {
        clearErrors("profilePicture"); // Clear the error if the file is valid
        const fileURL = URL.createObjectURL(selectedFile);
        setFilePreview(fileURL); // Set the preview URL
      }
    }
  };

  return (
    <>
      <Heading text="User Profile">
        <>
          {isReadOnly && (
            <IconButton
              Icon={PencilSquareIcon}
              text="Edit"
              onClick={setEditMode}
            />
          )}
          {!isReadOnly && (
            <>
              <IconButton
                Icon={XCircleIcon}
                text="Cancel"
                onClick={cancelEdit}
              />
              <IconButton
                Icon={ArrowDownOnSquareIcon}
                text="Save"
                onClick={handleSubmit(onSubmit)}
              />
            </>
          )}
        </>
      </Heading>
      <FormSection
        formDescription="View or edit user profile details."
        formHeading="User Details"
      >
        <FieldGroup>
          {isReadOnly && (
            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              <div className="sm:col-span-2">
                <Avatar
                  firstName={currentUser?.firstName ?? ''}
                  lastName={currentUser?.lastName ?? ''}
                  imageUrl={`/api/static/images/${currentUser?.profilePicture}`}
                  className="w-20 h-20"
                />
              </div>
              <div className="sm:col-span-2">
                <ReadOnlyFormElement
                  label="Name"
                  value={
                    currentUser
                      ? `${currentUser.title} ${currentUser.firstName} ${currentUser.lastName}`
                      : ""
                  }
                />
              </div>

              <div className="sm:col-span-2">
                <ReadOnlyFormElement
                  label="Phone Number"
                  value={currentUser?.phone}
                />
              </div>

              <div className="sm:col-span-2">
                <ReadOnlyFormElement
                  label="Email (username)"
                  value={currentUser?.email}
                />
              </div>
            </div>
          )}
          {!isReadOnly && (
            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              <div className="sm:col-span-6">
                <Field>
                  <Label>Profile Picture</Label>
                  <div className="flex gap-5 justify-center items-center">
                    <input
                      style={{ height: "39px", width: "109px" }}
                      id="profilePicture"
                      {...register("profilePicture")}
                      type="file"
                      onChange={handleFileChange}
                      accept="image/*"
                      className="block w-full text-sm text-gray-900 dark:text-gray-100 dark:bg-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-600 file:text-white hover:file:bg-indigo-500"
                    />

                    <Avatar
                      initials="NC"
                      src={
                        filePreview ??
                        `/api/static/images/${currentUser?.profilePicture}`
                      }
                      className="h-20 w-20 rounded-full " firstName={""} lastName={""} 
                    />
                  </div>
                  {errors.profilePicture && (
                    <ErrorMessage>
                      {errors.profilePicture?.message as string}
                    </ErrorMessage>
                  )}
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field>
                  <Label>Title</Label>
                  <Controller
                    name="title"
                    control={control}
                    rules={{ required: "Title is required" }}
                    render={({ field }) => (
                      <Listbox
                        name="title"
                        value={field.value}
                        invalid={!!errors.groupOrKnockout}
                        onChange={(e) => setValue("title", e)}
                      >
                        <ListboxOption value="">
                          <ListboxLabel></ListboxLabel>
                        </ListboxOption>
                        <ListboxOption value="Mr.">
                          <ListboxLabel>Mr.</ListboxLabel>
                        </ListboxOption>
                        <ListboxOption value="Ms.">
                          <ListboxLabel>Ms.</ListboxLabel>
                        </ListboxOption>
                        <ListboxOption value="Mrs.">
                          <ListboxLabel>Mrs.</ListboxLabel>
                        </ListboxOption>
                        <ListboxOption value="Dr.">
                          <ListboxLabel>Dr.</ListboxLabel>
                        </ListboxOption>
                      </Listbox>
                    )}
                  />
                  {errors.title && (
                    <ErrorMessage>
                      {errors.title?.message as string}
                    </ErrorMessage>
                  )}
                </Field>
              </div>

              <div className="sm:col-span-2">
                <Field>
                  <Label>First name</Label>
                  <Input
                    {...register("firstName", {
                      required: "First name is required",
                    })}
                    invalid={!!errors.firstName}
                  />
                  {errors.firstName && (
                    <ErrorMessage>
                      {errors.firstName?.message as string}
                    </ErrorMessage>
                  )}
                </Field>
              </div>

              <div className="sm:col-span-2">
                <Field>
                  <Label>Last name</Label>
                  <Input
                    {...register("lastName", {
                      required: "Last name is required",
                    })}
                    invalid={!!errors.lastName}
                  />
                  {errors.lastName && (
                    <ErrorMessage>
                      {errors.lastName?.message as string}
                    </ErrorMessage>
                  )}
                </Field>
              </div>

              <div className="sm:col-span-4">
                <Field>
                  <Label>Phone number</Label>
                  <Input
                    {...register("phone", {
                      required: "Phone number is required",
                    })}
                    invalid={!!errors.phone}
                  />
                  {errors.phone && (
                    <ErrorMessage>
                      {errors.phone?.message as string}
                    </ErrorMessage>
                  )}
                </Field>
              </div>
              <div className="sm:col-span-6">
                <Field>
                  <Label>Password</Label>
                  <div className="mt-3">
                    <Text>
                      To change your password, logout and click on the 'I forgot my password!' link.
                    </Text>
                  </div>
                </Field>
              </div>
            </div>
          )}
          <VerifyEmail />
        </FieldGroup>
      </FormSection>
    </>
  );
};

export default CurrentUser;
